import React, { useState } from 'react';

import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Menu from '@mui/material/Menu';
import MenuIcon from '@mui/icons-material/Menu';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import MenuItem from '@mui/material/MenuItem';

import Badge from '@mui/material/Badge';
import MailIcon from '@mui/icons-material/Mail';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SportsScoreIcon from '@mui/icons-material/SportsScore';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';

import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import TivityLogo from './TivityLogo';
import LanguageSwitcher from './LanguageSwitcher';
import MobileNavDrawer from './MobileNavDrawer';
import ClubSwitcher from './ClubSwitcher';

import { useMediaQuery } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAppDispatch, useAppSelector, useDecodedToken } from '../app/hooks';
import { setToken } from '../features/auth/authSlice';
import { useGetNbAdminRequestQuery, useGetNbPendingRacesQuery } from '../api/apiSlice';
import { stringToColor } from '../utils/stringToColor';

// Stable identifiers, decoupled from display label (which is translated at
// render time via nav.<key>) -- desktop nav buttons compare against these
// keys, not the label text.
const pages = ['outings', 'races', 'events', 'history'] as const;
const settings = ['myProfile', 'logout'] as const;

function ActivitiesBar() {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const isMdScreen = useMediaQuery('(min-width:600px)');

  const dispatch = useAppDispatch();
  const token = useAppSelector((state) => state.auth.token);

  const decodedToken = useDecodedToken();
  const NAME = decodedToken?.name ?? '';
  const FIRSTNAME = decodedToken?.firstname ?? '';
  const role = decodedToken?.role ?? null;
  const clubID = decodedToken?.clubId != null ? String(decodedToken.clubId) : '';

  const { data: adminRequests } = useGetNbAdminRequestQuery(clubID, { skip: !token || role !== 'ADMIN' });
  const badgeCounter = adminRequests?.[0]?.total_users ?? 0;

  const { data: pendingRaces } = useGetNbPendingRacesQuery(clubID, { skip: !token || role !== 'ADMIN' });
  const raceBadgeCounter = pendingRaces?.[0]?.total_races ?? 0;

  const navigate = useNavigate();

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleOpenNavMenu = () => {
    setDrawerOpen(true);
  };

  const handleCloseNavMenu = () => {
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    dispatch(setToken(null));
    navigate("/")
  };

  const handleCloseUserMenu = (event: React.SyntheticEvent, setting?: string) => {
    setAnchorElUser(null);
    if (setting === 'myProfile') {
      navigate('/profile');
    } else if (setting === 'logout') {
      handleLogout();
    }
  };

  const handleUserRequest = () => {
    navigate("/administration", { state: { selectedTab: 1 } });
    setDrawerOpen(false);
  };

  const handleRaceRequest = () => {
    navigate("/administration", { state: { selectedTab: 3 } });
    setDrawerOpen(false);
  };

  const handleReachMemberList = () => {
    navigate("/administration", { state: { selectedTab: 0 } });
    setDrawerOpen(false);
  };

  const drawerItems = [
    { key: 'outings', label: t('nav.outings'), onClick: () => navigate('/activities') },
    { key: 'races', label: t('nav.races'), onClick: () => navigate('/races') },
    { key: 'events', label: t('nav.events'), onClick: () => navigate('/events') },
    { key: 'history', label: t('nav.history'), onClick: () => navigate('/history') },
    ...(decodedToken?.isSuperAdmin
      ? [{ key: 'superadmin', label: t('superadmin.navLabel'), onClick: () => navigate('/superadmin') }]
      : []),
    { key: 'myProfile', label: t('nav.myProfile'), onClick: () => navigate('/profile') },
    { key: 'logout', label: t('nav.logout'), onClick: handleLogout },
  ];

  const handleDesktopPageClick = (page: string) => {
    if (page === "outings") navigate("/activities");
    else if (page === "races") navigate("/races");
    else if (page === "history") navigate("/history");
    else if (page === "events") navigate("/events");
  };

  return (
      <div >
        <AppBar position={isMdScreen ? "sticky" : "static"} color="primary">
          <Container maxWidth="xl" >
            <Toolbar disableGutters>
              <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  aria-label={t('nav.openMenu')}
                  aria-controls="menu-appbar"
                  aria-haspopup="true"
                  onClick={handleOpenNavMenu}
                  color="inherit"
                >
                  <MenuIcon />
                </IconButton>
              </Box>

              <Box sx={{display: 'flex', alignItems: 'center', flex: { xs: 2, md: 0 } }}>
                <Link href="/" underline="none">
                  <TivityLogo variant="light" />
                </Link>
              </Box>

                <MobileNavDrawer
                  open={drawerOpen}
                  onClose={handleCloseNavMenu}
                  items={drawerItems}
                  header={
                    <Grid container spacing={2} direction="column" alignItems="center" sx={{ pt: 4, pb: 2 }}>
                      <Grid item>
                        {NAME && FIRSTNAME ? (
                          <Avatar
                            sx={{ width: 100, height: 100, bgcolor: stringToColor(NAME), marginTop:'16px' }}
                          >
                            {`${FIRSTNAME[0]}${NAME[0]}`}
                          </Avatar>
                        ) : (
                          <Avatar>U</Avatar>
                        )}
                      </Grid>
                      <Grid item>
                        {NAME && FIRSTNAME ? (
                          <Typography variant="h6" textAlign="center">
                            {FIRSTNAME} {NAME}
                          </Typography>
                        ) : (
                          <Typography variant="h6" textAlign="center">
                            {t('activitiesBar.genericUser')}
                          </Typography>
                        )}
                      </Grid>

                      {role === 'ADMIN' && (
                      <Grid item>
                        <IconButton size="large" onClick={handleUserRequest} aria-label={t('administration.requests')}>
                          <Badge badgeContent={badgeCounter} color="error">
                            <MailIcon color="primary" fontSize='large'/>
                          </Badge>
                        </IconButton>

                        <IconButton size="large" onClick={handleRaceRequest} aria-label={t('administration.raceRequests')}>
                          <Badge badgeContent={raceBadgeCounter} color="error">
                            <SportsScoreIcon color="primary" fontSize='large'/>
                          </Badge>
                        </IconButton>

                        <IconButton size="large" onClick={handleReachMemberList} aria-label={t('administration.members')}>
                          <SupervisorAccountIcon color="primary"/>
                        </IconButton>
                      </Grid>
                    )}
                    </Grid>
                  }
                />

                <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
                  {pages.map((page) => (
                    <Button
                      key={page}
                      onClick={() => handleDesktopPageClick(page)}
                      sx={{ my: 2, color: 'white', display: 'block', paddingRight:1 }}
                    >
                      {t(`nav.${page}`)}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ flexGrow: 1 }} />
              <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
                {role === 'ADMIN' && (
                  <>
                    <IconButton size="large" onClick={handleUserRequest} aria-label={t('administration.requests')}>
                      <Badge badgeContent={badgeCounter} color="error">
                        <MailIcon color="icon" />
                      </Badge>
                    </IconButton>

                    <IconButton size="large" onClick={handleRaceRequest} aria-label={t('administration.raceRequests')}>
                      <Badge badgeContent={raceBadgeCounter} color="error">
                        <SportsScoreIcon color="icon" />
                      </Badge>
                    </IconButton>

                    <IconButton size="large" onClick={handleReachMemberList} aria-label={t('administration.members')}>
                      <SupervisorAccountIcon color="icon"/>
                    </IconButton>
                  </>
                )}

                {decodedToken?.isSuperAdmin && (
                  <IconButton size="large" onClick={() => navigate('/superadmin')} aria-label={t('superadmin.navLabel')}>
                    <MonitorHeartIcon color="icon" />
                  </IconButton>
                )}

                <Tooltip title={t('activitiesBar.openSettings')}>
                  <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }} aria-label={t('activitiesBar.openSettings')}>
                    {NAME && FIRSTNAME ? (
                      <Avatar sx={{ bgcolor: stringToColor(NAME) }}>{`${FIRSTNAME[0]}${NAME[0]}`}</Avatar>
                    ) : (
                      <Avatar>U</Avatar>
                    )}
                  </IconButton>
                </Tooltip>

                <Menu
                  sx={{ mt: '45px' }}
                  id="menu-appbar"
                  anchorEl={anchorElUser}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  keepMounted
                  transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                  }}
                  open={Boolean(anchorElUser)}
                  onClose={handleCloseUserMenu}
                >
                  {settings.map((setting) => (
                    <MenuItem key={setting} onClick={(event) => handleCloseUserMenu(event, setting)}>
                      <Typography textAlign="center">{t(`nav.${setting}`)}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                <ClubSwitcher />
                <LanguageSwitcher variant="light" />
              </Box>
            </Toolbar>
          </Container>
        </AppBar>
      </div>
  );
}
export default ActivitiesBar;
